import { supabase } from '../supabaseClient';

export const editionService = {
  async getEditions() {
    const { data, error } = await supabase
      .from('editions')
      .select('*')
      .order('year', { ascending: false });
    if (error) throw error;
    return data;
  },

  async createEdition(editionData) {
    const { data, error } = await supabase
      .from('editions')
      .insert([editionData])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async cloneEditionData(sourceEditionId, targetEditionId) {
    try {
      // List of tables to clone (data tables)
      const tablesToClone = [
        'apropos_data',
        'hero_data',
        'contact_data',
        'dates_data',
        'objectives_data',
        'participation_data',
        'speakers_data',
        'topics_data',
        'flyers_page_data',
        'programme_data',
        'historique_data',
        'challenge_data',
        'apsis_settings',
        'speakers'
      ];

      // Maps to handle ID translation
      const speakerIdMap = {};
      const cadreIdMap = {};

      for (const table of tablesToClone) {
        const { data: sourceData, error: fetchError } = await supabase
          .from(table)
          .select('*')
          .eq('edition_id', sourceEditionId);

        if (fetchError) {
          console.error(`Error fetching from ${table}:`, fetchError);
          continue;
        }

        if (sourceData && sourceData.length > 0) {
          const clonedData = sourceData.map(item => {
            const { id, created_at, updated_at, ...rest } = item;
            return {
              ...rest,
              edition_id: targetEditionId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
          });

          const { data: insertedData, error: insertError } = await supabase
            .from(table)
            .insert(clonedData)
            .select();

          if (insertError) {
            console.error(`Error inserting into ${table}:`, insertError);
          } else if (table === 'speakers' && insertedData) {
            // Build map: old ID -> new ID
            sourceData.forEach((oldItem, index) => {
              speakerIdMap[oldItem.id] = insertedData[index].id;
            });
          }
        }
      }

      // Clone related lists (sub-entities)
      const listTables = [
        'apropos_event_details',
        'dates_key_dates',
        'objectives',
        'topics',
        'flyers',
        'media_images',
        'media_videos',
        'social_media_images',
        'cadre_conference',
        'challenge_members',
        'apropos_committee_members'
      ];

      for (const table of listTables) {
        const { data: sourceData, error: fetchError } = await supabase
          .from(table)
          .select('*')
          .eq('edition_id', sourceEditionId);

        if (fetchError) {
          console.error(`Error fetching lists from ${table}:`, fetchError);
          continue;
        }

        if (sourceData && sourceData.length > 0) {
          const clonedData = sourceData.map(item => {
            const { id, created_at, updated_at, ...rest } = item;
            return {
              ...rest,
              edition_id: targetEditionId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
          });

          const { data: insertedData, error: insertError } = await supabase
            .from(table)
            .insert(clonedData)
            .select();

          if (insertError) {
            console.error(`Error inserting into ${table}:`, insertError);
          } else if (table === 'cadre_conference' && insertedData) {
            // Build map for cadres (old ID -> new ID)
            sourceData.forEach((oldItem, index) => {
              cadreIdMap[oldItem.id] = insertedData[index].id;
            });
          }
        }
      }

      // Now clone junction table speaker_cadres using our maps
      const { data: sourceJunctions } = await supabase
        .from('speaker_cadres')
        .select('*')
        .in('speaker_id', Object.keys(speakerIdMap));

      if (sourceJunctions && sourceJunctions.length > 0) {
        const clonedJunctions = sourceJunctions
          .map(item => {
            const newSpeakerId = speakerIdMap[item.speaker_id];
            const newCadreId = cadreIdMap[item.cadre_id];
            
            if (newSpeakerId && newCadreId) {
              return {
                speaker_id: newSpeakerId,
                cadre_id: newCadreId,
                display_order: item.display_order,
                created_at: new Date().toISOString()
              };
            }
            return null;
          })
          .filter(Boolean);

        if (clonedJunctions.length > 0) {
          await supabase.from('speaker_cadres').insert(clonedJunctions);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error in cloneEditionData:', error);
      throw error;
    }
  }
};
